/**
 * Blog editor right-rail metadata — the canonical URL.
 *
 * Writes the canonical URL through the same `blogCreationService.patchContent`
 * the rail form calls. `writes-config`: it edits our row's metadata and reaches
 * the public page only if and when the article is later published, so it is
 * strictly lighter than `ed_publish_cms`.
 */
import { z } from 'zod';
import {
  defineCommand,
  refuse,
  type CommitAuthorization,
  type Diff,
  type Receipt,
  type Refusal,
  type ResolveInput,
  type TenantId,
  type WriteDeps,
} from '../contract';

const setCanonicalArgs = z.object({
  url: z
    .string()
    .url()
    .describe('The absolute canonical URL, for example "https://client.com/blog/my-post".'),
});
type SetCanonicalArgs = z.infer<typeof setCanonicalArgs>;

interface CanonicalTarget {
  readonly contentId: string;
  readonly url: string;
}

export const edSetCanonical = defineCommand<SetCanonicalArgs, CanonicalTarget>({
  name: 'ed_set_canonical',
  section: 'editor-meta',
  consequence: 'writes-config',
  description:
    'Set the canonical URL for the open blog article, the same field as the rail Publishing card. It edits the article metadata in Core and takes effect on the public page when the article is published.',
  args: setCanonicalArgs,

  async resolve(input: ResolveInput<SetCanonicalArgs>): Promise<CanonicalTarget | Refusal> {
    const { args, tenant, read } = input;
    if (!read.editorContext) {
      return refuse('This surface cannot see the blog editor.');
    }
    const ctx = read.editorContext(tenant);
    if (!ctx) {
      return refuse('No blog editor is open in this tab.', { retryable: true });
    }
    return { contentId: ctx.contentId, url: args.url };
  },

  diff(target: CanonicalTarget, tenant: TenantId): Diff {
    return {
      table: 'blog_content',
      rowId: target.contentId,
      tenant,
      // `SEAM_BINDINGS.editorSetCanonical` reproduces `{ canonical_url: <url> }`.
      changes: [
        { field: 'canonical_url', from: null, to: target.url, authorability: 'exact' },
      ],
      reachesGeneratedContent: false,
    };
  },

  async run(
    target: CanonicalTarget,
    auth: CommitAuthorization,
    write: WriteDeps,
  ): Promise<Receipt | Refusal> {
    if (!write.editorSetCanonical) {
      return refuse('This surface cannot change the canonical URL.');
    }
    return write.editorSetCanonical(auth, target.url);
  },
});

/* -------------------------------------------------------------------------- */
/* ed_set_jsonld — the structured-data (JSON-LD) description                    */
/* -------------------------------------------------------------------------- */

const setJsonldArgs = z.object({
  description: z
    .string()
    .min(1)
    .max(2000)
    .describe('The article schema (JSON-LD) description, one or two sentences of plain prose.'),
});
type SetJsonldArgs = z.infer<typeof setJsonldArgs>;

interface JsonldTarget {
  readonly contentId: string;
  readonly description: string;
}

export const edSetJsonld = defineCommand<SetJsonldArgs, JsonldTarget>({
  name: 'ed_set_jsonld',
  section: 'editor-meta',
  consequence: 'writes-config',
  description:
    "Set the article's JSON-LD schema description, the same field as the rail Structured Data card. It edits the article's schema metadata in Core and takes effect on the public page when the article is published.",
  args: setJsonldArgs,

  async resolve(input: ResolveInput<SetJsonldArgs>): Promise<JsonldTarget | Refusal> {
    const { args, tenant, read } = input;
    if (!read.editorContext) {
      return refuse('This surface cannot see the blog editor.');
    }
    const ctx = read.editorContext(tenant);
    if (!ctx) {
      return refuse('No blog editor is open in this tab.', { retryable: true });
    }
    return { contentId: ctx.contentId, description: args.description };
  },

  diff(target: JsonldTarget, tenant: TenantId): Diff {
    return {
      table: 'blog_content',
      rowId: target.contentId,
      tenant,
      // `SEAM_BINDINGS.editorSetJsonldDescription` reproduces `{ schema_description: <text> }`.
      changes: [
        { field: 'schema_description', from: null, to: target.description, authorability: 'prose' },
      ],
      reachesGeneratedContent: false,
    };
  },

  async run(
    target: JsonldTarget,
    auth: CommitAuthorization,
    write: WriteDeps,
  ): Promise<Receipt | Refusal> {
    if (!write.editorSetJsonldDescription) {
      return refuse('This surface cannot change the JSON-LD schema.');
    }
    return write.editorSetJsonldDescription(auth, target.description);
  },
});

/* -------------------------------------------------------------------------- */
/* ed_set_og_image — the Open Graph / social-share image                       */
/* -------------------------------------------------------------------------- */

const setOgImageArgs = z.object({
  url: z
    .string()
    .url()
    .describe('The absolute image URL for social sharing, for example "https://client.com/hero.jpg".'),
});
type SetOgImageArgs = z.infer<typeof setOgImageArgs>;

interface OgImageTarget {
  readonly contentId: string;
  readonly url: string;
}

export const edSetOgImage = defineCommand<SetOgImageArgs, OgImageTarget>({
  name: 'ed_set_og_image',
  section: 'editor-meta',
  consequence: 'writes-config',
  description:
    "Set the article's Open Graph (social-share) image URL, the same field as the rail image card. It edits the article metadata in Core and is what social platforms show when the published article is shared.",
  args: setOgImageArgs,

  async resolve(input: ResolveInput<SetOgImageArgs>): Promise<OgImageTarget | Refusal> {
    const { args, tenant, read } = input;
    if (!read.editorContext) {
      return refuse('This surface cannot see the blog editor.');
    }
    const ctx = read.editorContext(tenant);
    if (!ctx) {
      return refuse('No blog editor is open in this tab.', { retryable: true });
    }
    return { contentId: ctx.contentId, url: args.url };
  },

  diff(target: OgImageTarget, tenant: TenantId): Diff {
    return {
      table: 'blog_content',
      rowId: target.contentId,
      tenant,
      // `SEAM_BINDINGS.editorSetOgImage` reproduces `{ og_image_url: <url> }`.
      changes: [{ field: 'og_image_url', from: null, to: target.url, authorability: 'exact' }],
      reachesGeneratedContent: false,
    };
  },

  async run(
    target: OgImageTarget,
    auth: CommitAuthorization,
    write: WriteDeps,
  ): Promise<Receipt | Refusal> {
    if (!write.editorSetOgImage) {
      return refuse('This surface cannot change the Open Graph image.');
    }
    return write.editorSetOgImage(auth, target.url);
  },
});
